<script lang="ts" setup>
import { ref, onUnmounted, computed, nextTick } from 'vue'
import { useQuasar } from 'quasar'
import { useIncidentSubmissionStore } from '../../stores/incident-submission'
import { useFileUploadStore } from '../../stores/file-upload'
import { getExtractor } from '@/utils/vendors/registry'
import {
  describePickNote,
  parseTeslaSelection,
  pickNotesHeading,
  type PickNote
} from '@/utils/vendors/tesla-flat'
import type { VendorEvent } from '@/utils/vendors/types'
import { TESLA_CATEGORY_LABELS } from '@/utils/vendors/tesla'
import { formatDateTime } from '@/utils/format'
import { regionConfig } from '@/config/region'
import TeslaFilePicker, { type PickedFiles } from '@/components/teslacam/TeslaFilePicker.vue'
import EventFileSelectDialog from './EventFileSelectDialog.vue'
import VendorEventMap from './VendorEventMap.vue'

const $q = useQuasar()
const incidentStore = useIncidentSubmissionStore()
const fileUploadStore = useFileUploadStore()

const extractor = computed(() => getExtractor(incidentStore.incidentForm.deviceVendor))

const pickNotes = ref<PickNote[]>([])
const isParsing = ref(false)
const events = ref<VendorEvent[]>([])
const selectedEventId = ref<string | null>(null)
const folderName = ref<string | null>(null)
const selectedReasons = ref<string[]>([])
const selectedCategory = ref<string | null>(null)
const mapFilterIds = ref<string[] | null>(null)

// Cached thumbnail object URLs keyed by event ID
const thumbnailUrls = new Map<string, string>()

onUnmounted(() => {
  thumbnailUrls.forEach((url) => URL.revokeObjectURL(url))
})

// Unique reason labels across all events — used by the filter
const allReasons = computed(() => [...new Set(events.value.map((e) => e.displayLabel))].sort())

// Categories present in the loaded events, in display order
const availableCategories = computed(() => {
  const keys = [...new Set(events.value.map((e) => (e.rawMeta as any)?.category).filter(Boolean))]
  return keys.sort((a, b) => {
    const order = Object.keys(TESLA_CATEGORY_LABELS)
    return order.indexOf(a) - order.indexOf(b)
  })
})

const filteredEvents = computed(() => {
  let result = events.value
  if (selectedCategory.value !== null) {
    result = result.filter((e) => (e.rawMeta as any)?.category === selectedCategory.value)
  }
  if (mapFilterIds.value !== null) {
    const idSet = new Set(mapFilterIds.value)
    result = result.filter((e) => idSet.has(e.id))
  }
  if (selectedReasons.value.length > 0) {
    result = result.filter((e) => selectedReasons.value.includes(e.displayLabel))
  }
  return result
})

function clearFolder() {
  thumbnailUrls.forEach((url) => URL.revokeObjectURL(url))
  thumbnailUrls.clear()
  events.value = []
  selectedEventId.value = null
  folderName.value = null
  selectedReasons.value = []
  selectedCategory.value = null
  mapFilterIds.value = null
  pickNotes.value = []
}

async function onPicked(picked: PickedFiles) {
  folderName.value = picked.name

  isParsing.value = true
  thumbnailUrls.forEach((url) => URL.revokeObjectURL(url))
  thumbnailUrls.clear()
  events.value = []
  selectedEventId.value = null
  selectedReasons.value = []
  selectedCategory.value = null
  mapFilterIds.value = null
  pickNotes.value = []

  try {
    const { events: parsed, notes } = await parseTeslaSelection(picked.files)
    pickNotes.value = notes

    if (parsed.length === 0) {
      folderName.value = null
      $q.notify({
        type: 'warning',
        message: [
          picked.mode === 'files'
            ? 'No TeslaCam events found in those files. Open one event folder and select all its files.'
            : 'No events found in that folder. Make sure you selected the TeslaCam or SavedClips folder.',
          ...notes.map(describePickNote)
        ].join(' '),
        position: 'top',
        timeout: 6000,
        actions: [{ label: 'OK', color: 'white' }]
      })
      return
    }

    // Pre-cache thumbnail object URLs to avoid creating duplicates on each render
    parsed.forEach((event) => {
      if (event.thumbnailFile) {
        thumbnailUrls.set(event.id, URL.createObjectURL(event.thumbnailFile))
      }
    })
    events.value = parsed
  } catch (err) {
    folderName.value = null
    $q.notify({
      type: 'negative',
      message: 'Failed to read the selected folder. Please try again.',
      position: 'top'
    })
    console.error('parseEvents error', err)
  } finally {
    isParsing.value = false
  }
}

function getEventLocation(event: VendorEvent): string {
  const meta = event.rawMeta as any
  if (meta?.place) return meta.place
  if (event.location) return `${event.location.lat.toFixed(4)}, ${event.location.lon.toFixed(4)}`
  return 'Location unknown'
}

function checkOutOfBounds(event: VendorEvent): string | undefined {
  const warnings: string[] = []

  if (event.dateTime) {
    const tenYearsAgo = new Date()
    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10)
    if (event.dateTime < tenYearsAgo) {
      warnings.push(
        `The event timestamp (${formatDateTime(event.dateTime)}) is more than 10 years in the past.`
      )
    }
  }

  if (event.location) {
    const { sw, ne } = regionConfig.mapBounds
    const { lat, lon } = event.location
    if (lat < sw.lat || lat > ne.lat || lon < sw.lng || lon > ne.lng) {
      warnings.push('The event location appears to be outside the supported region.')
    }
  }

  return warnings.length > 0 ? warnings.join(' ') : undefined
}

async function applyEvent(event: VendorEvent, selectedFiles: File[]) {
  if (selectedFiles.length > 0) {
    const addedFiles = fileUploadStore.addFilesForUpload(selectedFiles)

    const primaryFile = extractor.value.suggestPrimaryFile?.(selectedFiles, event)
    if (primaryFile) {
      const match = addedFiles.find((f) => f.file === primaryFile)
      if (match) fileUploadStore.setPrimaryFile(match.internalId)
    }

    await fileUploadStore.tryUploadFiles()
  }

  incidentStore.applyDashcamMetadata({
    dateTime: event.dateTime,
    lat: event.location?.lat,
    lon: event.location?.lon
  })

  const suggestion = extractor.value.suggestIncidentContext?.(event)
  if (suggestion) {
    if (suggestion.incidentType && !incidentStore.incidentForm.incidentReportType) {
      incidentStore.incidentForm.incidentReportType = suggestion.incidentType as any
    }
    if (suggestion.description && !incidentStore.incidentForm.userSubmittedIncidentDescription) {
      incidentStore.incidentForm.userSubmittedIncidentDescription = suggestion.description
      // nextTick ensures any mounted watcher that clears this hint fires first
      await nextTick()
      incidentStore.incidentForm.extractedMetadataHints.description = true
    }
  }

  incidentStore.nextStep()
}

function onCardClick(eventId: string) {
  selectedEventId.value = eventId
  const event = events.value.find((e) => e.id === eventId)
  if (!event) return

  $q.dialog({
    component: EventFileSelectDialog,
    componentProps: {
      event,
      outOfBoundsWarning: checkOutOfBounds(event),
      applyFiles: (selectedFiles: File[]) => applyEvent(event, selectedFiles),
      skipToManual: () => {
        selectedEventId.value = null
        incidentStore.nextStep()
      }
    }
  }).onCancel(() => {
    selectedEventId.value = null
  })
}

function onChangeFolder() {
  clearFolder()
}
</script>

<template>
  <div class="row q-col-gutter-y-lg">
    <!-- Intro -->
    <div class="col-12">
      <h6 class="text-h6 q-mt-none q-mb-sm text-blue-grey-9" tabindex="-1" data-step-heading>
        Browse your dashcam footage
      </h6>
      <p class="text-body2 text-blue-grey-8">
        Select your <strong>{{ extractor.vendorLabel }}</strong> footage folder to browse saved
        events. We'll extract the location and timestamp automatically.
      </p>
    </div>

    <!-- Picker — shown until a folder is loaded -->
    <div class="col-12">
      <!-- No folder selected yet -->
      <tesla-file-picker v-show="!folderName && !isParsing" @picked="onPicked" />

      <!-- Parsing in progress -->
      <template v-if="isParsing">
        <div class="row items-center no-wrap q-gutter-x-sm">
          <q-spinner-dots color="blue-8" size="1.6em" />
          <span class="text-body2 text-blue-grey-8">Reading folder…</span>
        </div>
      </template>

      <!-- Folder loaded — show name + change option -->
      <template v-else-if="folderName">
        <div class="row items-center no-wrap q-gutter-x-sm">
          <q-icon name="mdi-folder-check" color="blue-8" size="sm" />
          <span class="text-body2 text-blue-grey-9 text-weight-medium">{{ folderName }}</span>
          <q-btn
            flat
            dense
            size="sm"
            color="blue-grey-6"
            label="Change folder"
            icon="mdi-folder-open-outline"
            class="q-ml-xs"
            @click="onChangeFolder"
          />
        </div>
      </template>
    </div>

    <!-- Files that were missing or skipped -->
    <div v-if="events.length > 0 && pickNotes.length > 0" class="col-12">
      <q-banner rounded class="bg-orange-1 text-blue-grey-9" data-testid="vendor-pick-notes">
        <template #avatar><q-icon name="mdi-alert-circle-outline" color="orange-9" /></template>
        <div class="text-weight-bold">{{ pickNotesHeading(pickNotes) }}</div>
        <div v-for="note in pickNotes" :key="describePickNote(note)">
          {{ describePickNote(note) }}
        </div>
      </q-banner>
    </div>

    <!-- Map -->
    <div v-if="events.length > 0" class="col-12">
      <vendor-event-map
        :events="events"
        :selected-ids="mapFilterIds"
        @select="mapFilterIds = $event"
        @clear="mapFilterIds = null"
      />
    </div>

    <!-- Filter + event count row -->
    <template v-if="events.length > 0">
      <div class="col-12">
        <div class="row items-center q-col-gutter-md">
          <!-- Category select — only when multiple categories are present -->
          <div v-if="availableCategories.length > 1" class="col-12 col-sm-auto">
            <q-select
              v-model="selectedCategory"
              :options="[
                { label: 'All categories', value: null },
                ...availableCategories.map((c) => ({
                  label: TESLA_CATEGORY_LABELS[c] ?? c,
                  value: c
                }))
              ]"
              emit-value
              map-options
              outlined
              dense
              color="blue-8"
              label="Category"
              label-color="blue-grey-7"
              style="min-width: 180px"
            >
              <template v-if="selectedCategory !== null" v-slot:append>
                <q-icon
                  name="mdi-close-circle"
                  size="xs"
                  color="blue-grey-5"
                  class="cursor-pointer self-center"
                  @click.stop="selectedCategory = null"
                />
              </template>
            </q-select>
          </div>

          <!-- Type filter select — only when multiple event types are present -->
          <div v-if="allReasons.length > 1" class="col-12 col-sm-auto">
            <q-select
              v-model="selectedReasons"
              :options="allReasons"
              multiple
              use-chips
              outlined
              dense
              color="blue-8"
              label="Filter by type"
              label-color="blue-grey-7"
              style="min-width: 220px"
            >
              <template v-if="selectedReasons.length > 0" v-slot:append>
                <q-icon
                  name="mdi-close-circle"
                  size="xs"
                  color="blue-grey-5"
                  class="cursor-pointer self-center"
                  @click.stop="selectedReasons = []"
                />
              </template>
            </q-select>
          </div>

          <!-- Event count — after the selects -->
          <div class="col-12 col-sm-auto">
            <span class="text-subtitle2 text-blue-grey-7">
              {{ filteredEvents.length }} of {{ events.length }} event{{
                events.length !== 1 ? 's' : ''
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- Event grid -->
      <div class="col-12">
        <div
          v-if="filteredEvents.length === 0"
          class="text-body2 text-blue-grey-6 q-py-md text-center"
        >
          No events match the current filter.
        </div>

        <div v-else class="row q-col-gutter-md">
          <div
            v-for="event in filteredEvents"
            :key="event.id"
            class="col-12 col-sm-6 col-md-4 col-lg-3"
          >
            <q-card
              v-ripple
              flat
              bordered
              class="cursor-pointer event-card transition-border full-height"
              :class="selectedEventId === event.id ? 'border-blue bg-blue-1' : 'bg-white'"
              @click="onCardClick(event.id)"
            >
              <!-- Thumbnail -->
              <div
                class="event-thumb bg-blue-grey-2 row items-center justify-center overflow-hidden"
              >
                <img
                  v-if="thumbnailUrls.has(event.id)"
                  :src="thumbnailUrls.get(event.id)"
                  class="fit-cover"
                  alt=""
                />
                <q-icon v-else name="mdi-video-outline" size="2.5em" color="blue-grey-4" />
              </div>

              <q-card-section class="q-pa-sm">
                <div class="text-caption text-blue-grey-6">
                  {{ event.dateTime ? formatDateTime(event.dateTime) : 'Unknown time' }}
                </div>
                <div class="text-body2 text-blue-grey-9 text-weight-medium ellipsis q-mt-xs">
                  {{ getEventLocation(event) }}
                </div>
                <div class="q-mt-xs">
                  <q-badge color="blue-grey-7" class="text-caption">
                    {{ event.displayLabel }}
                  </q-badge>
                </div>
              </q-card-section>

              <q-icon
                v-if="selectedEventId === event.id"
                name="mdi-check-circle"
                color="blue-8"
                size="sm"
                class="absolute-top-right q-ma-xs"
              />
            </q-card>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.event-thumb {
  height: 110px;
  width: 100%;
}

.fit-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.event-card {
  position: relative;
}

.transition-border {
  transition: all 0.2s ease-in-out;
}

.border-blue {
  border-color: var(--q-blue-8) !important;
  border-width: 2px !important;
}
</style>
